import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import ReviewMediationAnimation from "@/components/ReviewMediationAnimation";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Paperclip,
  FileText,
  Image as ImageIcon,
  Music,
  Video,
  Mic,
  Square,
  X,
  ShieldAlert,
  Gavel,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const MAX_FILES = 4;

// Per-type raw size caps (must mirror backend limits in server.py).
const SIZE_CAPS = {
  image: 2_000_000,
  "application/pdf": 2_000_000,
  audio: 3_000_000,
  video: 5_000_000,
};

function capFor(mime) {
  if (!mime) return 1_500_000;
  const m = mime.toLowerCase();
  if (m.startsWith("image/")) return SIZE_CAPS.image;
  if (m === "application/pdf") return SIZE_CAPS["application/pdf"];
  if (m.startsWith("audio/")) return SIZE_CAPS.audio;
  if (m.startsWith("video/")) return SIZE_CAPS.video;
  return 1_500_000;
}

function readAsDataURL(fileOrBlob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(fileOrBlob);
  });
}

function friendlySize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1_000_000) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

function FileIcon({ mime, size = 14 }) {
  if (!mime) return <FileText size={size} />;
  if (mime.startsWith("image/")) return <ImageIcon size={size} />;
  if (mime.startsWith("audio/")) return <Music size={size} />;
  if (mime.startsWith("video/")) return <Video size={size} />;
  return <FileText size={size} />;
}

// Compact media renderer used in the "Evidence on file" list.
function EvidenceMedia({ file }) {
  const { mime, data_url, name } = file;
  if (mime?.startsWith("image/")) {
    return (
      <a
        href={data_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block border border-[#EAE3DE] rounded-lg overflow-hidden hover:border-[#B31E1E] hover:shadow-md transition-all"
        title={name}
      >
        <img src={data_url} alt={name} className="h-24 w-24 object-cover" />
      </a>
    );
  }
  if (mime?.startsWith("audio/")) {
    return (
      <div className="flex items-center gap-2 border border-[#EAE3DE] rounded-lg px-3 py-2 bg-white">
        <Music size={14} className="text-[#B31E1E] shrink-0" />
        <audio src={data_url} controls className="h-8 min-w-[180px]" />
      </div>
    );
  }
  if (mime?.startsWith("video/")) {
    return (
      <div className="border border-[#EAE3DE] rounded-lg overflow-hidden bg-black">
        <video src={data_url} controls className="max-h-40 w-auto" />
      </div>
    );
  }
  return (
    <a
      href={data_url}
      target="_blank"
      rel="noopener noreferrer"
      download={name}
      className="flex items-center gap-2 border border-[#EAE3DE] rounded-lg px-3 py-2 text-xs hover:border-[#B31E1E] hover:shadow-md transition-all bg-white"
    >
      <FileText size={14} className="text-[#B31E1E]" /> {name}
    </a>
  );
}

function AudioRecorder({ onCaptured, disabled }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(
    () => () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRef.current && mediaRef.current.state !== "inactive") {
        mediaRef.current.stop();
      }
    },
    [],
  );

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Audio recording isn't supported by this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mime = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data && e.data.size && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        const blob = new Blob(chunksRef.current, { type: mime || "audio/webm" });
        if (blob.size > SIZE_CAPS.audio) {
          toast.error(`Recording is ${friendlySize(blob.size)} — cap is ${SIZE_CAPS.audio / 1_000_000}MB`);
          return;
        }
        const data_url = await readAsDataURL(blob);
        onCaptured({
          name: `recording-${new Date().toISOString().replace(/[:.]/g, "-")}.webm`,
          mime: blob.type || "audio/webm",
          data_url,
        });
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      toast.error("Could not access microphone: " + (e.message || "permission denied"));
    }
  };

  const stop = () => {
    if (mediaRef.current && mediaRef.current.state !== "inactive") {
      mediaRef.current.stop();
    }
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  return recording ? (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={stop}
      className="rounded-lg border-[#B31E1E] text-[#B31E1E] hover:bg-[#F9E4E1]"
      data-testid="dispute-audio-stop"
    >
      <Square size={13} className="mr-1.5 fill-current" /> Stop ({elapsed}s)
    </Button>
  ) : (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={start}
      disabled={disabled}
      className="rounded-lg"
      data-testid="dispute-audio-start"
    >
      <Mic size={13} className="mr-1.5" /> Record audio
    </Button>
  );
}

function MediationBadge({ mediation }) {
  if (!mediation) return null;
  const map = {
    open: { label: "Evidence submitted", cls: "bg-[#FEF3E7] text-[#8A4B00] border-[#F5D9A5]" },
    in_review: { label: "In mediation", cls: "bg-[#F9E4E1] text-[#B31E1E] border-[#F0C9C4]" },
    resolved: { label: "Resolved", cls: "bg-[#E7F5EC] text-[#0F6C34] border-[#B7E1C5]" },
    dismissed: { label: "Dismissed", cls: "bg-[#F1F1F1] text-[#555] border-[#DDD]" },
  };
  const s = map[mediation.status] || map.open;
  return (
    <Badge className={`rounded-full font-mono text-[10px] px-2 py-0.5 border ${s.cls}`} variant="outline">
      <Gavel size={10} className="mr-1" /> {s.label}
    </Badge>
  );
}

export function DisputeBlock({ review, currentUser, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [escalateOpen, setEscalateOpen] = useState(false);
  const [escalateReason, setEscalateReason] = useState("");
  const [escalating, setEscalating] = useState(false);
  const inputRef = useRef(null);

  const canAdd =
    currentUser &&
    (currentUser.id === review.author_id || currentUser.id === review.target_id);

  const disputes = review.disputes || [];
  const mediation = review.mediation;
  const canEscalate =
    canAdd && disputes.length > 0 && (!mediation || mediation.status === "open");

  const handleFiles = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    if (files.length + list.length > MAX_FILES) {
      toast.error(`Max ${MAX_FILES} files per submission`);
      return;
    }
    const next = [...files];
    for (const f of list) {
      const cap = capFor(f.type);
      if (f.size > cap) {
        toast.error(`'${f.name}' is ${friendlySize(f.size)} — cap for this type is ${cap / 1_000_000}MB`);
        continue;
      }
      const okMime =
        f.type.startsWith("image/") ||
        f.type.startsWith("audio/") ||
        f.type.startsWith("video/") ||
        f.type === "application/pdf";
      if (!okMime) {
        toast.error(`'${f.name}' — allowed: image, PDF, audio, video`);
        continue;
      }
      // eslint-disable-next-line no-await-in-loop -- sequential reads keep base64 payload order stable
      const data_url = await readAsDataURL(f);
      next.push({ name: f.name, mime: f.type, data_url });
    }
    setFiles(next.slice(0, MAX_FILES));
    if (inputRef.current) inputRef.current.value = "";
  };

  const addRecorded = (file) => {
    if (files.length >= MAX_FILES) {
      toast.error(`Max ${MAX_FILES} files per submission`);
      return;
    }
    setFiles([...files, file]);
  };

  const removeFile = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!comment.trim() && files.length === 0) {
      toast.error("Add a comment or at least one file");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/reviews/${review.id}/dispute`, { comment, files });
      toast.success("Evidence submitted");
      setComment("");
      setFiles([]);
      setOpen(false);
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const escalate = async () => {
    if (escalateReason.trim().length < 10) {
      toast.error("Please explain why you're requesting mediation (10+ chars)");
      return;
    }
    setEscalating(true);
    try {
      await api.post(`/reviews/${review.id}/mediation/escalate`, { reason: escalateReason });
      toast.success("Escalated — an admin will review shortly");
      setEscalateOpen(false);
      setEscalateReason("");
      onUpdated?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setEscalating(false);
    }
  };

  return (
    <div className="mt-4 border-t border-[#EAE3DE] pt-4" data-testid={`dispute-block-${review.id}`}>
      {(disputes.length > 0 || mediation) && (
        <div className="flex items-center justify-between mb-3">
          <p className="text-[11px] uppercase tracking-wider font-semibold text-[#5C4E48] flex items-center gap-1.5">
            <ShieldAlert size={12} /> Mediation record{disputes.length ? ` · ${disputes.length} submission${disputes.length > 1 ? "s" : ""}` : ""}
          </p>
          <MediationBadge mediation={mediation} />
        </div>
      )}

      {mediation?.status === "resolved" && mediation.verdict && (
        <div className="mb-3 rounded-lg border border-[#B7E1C5] bg-[#E7F5EC] p-3 text-sm text-[#0F6C34]">
          <div className="font-semibold flex items-center gap-1.5">
            <Gavel size={13} /> Admin verdict{mediation.admin_name ? ` · ${mediation.admin_name}` : ""}
          </div>
          <p className="mt-1 whitespace-pre-wrap">{mediation.verdict}</p>
          {mediation.action && <p className="mt-1.5 text-xs opacity-80">Action: {mediation.action}</p>}
        </div>
      )}

      {disputes.length > 0 && (
        <div className="space-y-3 mb-3">
          {disputes.map((d) => (
            <div
              key={d.id}
              className="bg-[#FAF5F2] border border-[#EAE3DE] rounded-lg p-3"
              data-testid={`dispute-item-${d.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-mono text-[#3E2A26]">
                  {d.author_name} · {d.author_role === "business" ? "Business" : "Customer"}
                  {d.side === "target" ? " (subject)" : " (author)"}
                </span>
                <span className="text-[10px] font-mono text-[#8B7B73]">
                  {new Date(d.created_at).toLocaleString()}
                </span>
              </div>
              {d.comment && (
                <p className="mt-2 text-sm text-[#1B1614] leading-relaxed whitespace-pre-wrap">
                  {d.comment}
                </p>
              )}
              {d.files?.length > 0 && (
                <ul className="mt-3 flex flex-wrap gap-2">
                  {d.files.map((f, i) => (
                    <li key={`${d.id}-${i}`} data-testid={`dispute-file-${d.id}-${i}`}>
                      <EvidenceMedia file={f} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {canAdd && !open && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-lg border-[#EAE3DE] hover:bg-[#FAF5F2]"
            onClick={() => setOpen(true)}
            data-testid={`dispute-open-${review.id}`}
          >
            <Paperclip size={14} className="mr-1.5" /> Add evidence
          </Button>
        )}
        {canEscalate && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-lg border-[#B31E1E] text-[#B31E1E] hover:bg-[#F9E4E1]"
            onClick={() => setEscalateOpen(true)}
            data-testid={`dispute-escalate-${review.id}`}
          >
            <Gavel size={14} className="mr-1.5" /> Request mediation
          </Button>
        )}
      </div>

      {canAdd && open && (
        <div className="mt-3 space-y-3 bg-white border border-[#EAE3DE] rounded-lg p-4">
          {/* Inline explainer — reassures the user their evidence goes
              somewhere real by animating the full mediation lifecycle. */}
          <div className="mb-1" data-testid={`dispute-explainer-${review.id}`}>
            <div className="flex items-center gap-2 mb-2">
              <ShieldAlert size={13} className="text-[#B31E1E]" />
              <p className="text-[11px] uppercase tracking-wider font-semibold text-[#5C4E48]">
                What happens after you submit
              </p>
            </div>
            <ReviewMediationAnimation />
            <p className="mt-2 text-[11px] text-[#8B7B73] leading-relaxed">
              Once submitted, we notify the other party with your evidence and give them a fair window to respond.
              If they decline to respond or can&apos;t substantiate their position, the review is removed.
            </p>
          </div>

          <Textarea
            rows={3}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Describe the disagreement, quote a text message, or add context for your evidence."
            className="rounded-lg"
            data-testid={`dispute-comment-${review.id}`}
          />
          <div className="flex flex-wrap gap-2 items-center">
            <input
              ref={inputRef}
              type="file"
              multiple
              accept="image/*,application/pdf,audio/*,video/*"
              onChange={handleFiles}
              className="hidden"
              data-testid={`dispute-file-input-${review.id}`}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-lg"
              onClick={() => inputRef.current?.click()}
              data-testid={`dispute-pick-${review.id}`}
            >
              <Paperclip size={14} className="mr-1.5" /> Attach files
            </Button>
            <AudioRecorder onCaptured={addRecorded} disabled={files.length >= MAX_FILES} />
            <span className="text-[11px] text-[#8B7B73]">
              Up to {MAX_FILES} files · image 2MB · audio 3MB · video 5MB · PDF 2MB
            </span>
          </div>

          {files.length > 0 && (
            <ul className="space-y-1.5">
              {files.map((f, i) => (
                <li
                  key={`new-${i}`}
                  className="flex items-center gap-2 text-xs text-[#3E2A26] bg-[#FAF5F2] border border-[#EAE3DE] rounded-md px-2 py-1.5"
                >
                  <FileIcon mime={f.mime} size={13} />
                  <span className="flex-1 truncate">{f.name}</span>
                  <span className="text-[10px] font-mono text-[#8B7B73]">
                    {friendlySize(Math.floor(((f.data_url?.length || 0) * 3) / 4))}
                  </span>
                  <button
                    type="button"
                    onClick={() => removeFile(i)}
                    className="text-[#8B7B73] hover:text-[#B31E1E]"
                    aria-label={`Remove ${f.name}`}
                    data-testid={`dispute-remove-file-${i}`}
                  >
                    <X size={13} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={submitting}
              onClick={submit}
              className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white"
              data-testid={`dispute-submit-${review.id}`}
            >
              {submitting ? "Submitting..." : "Submit evidence"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="rounded-lg"
              onClick={() => {
                setOpen(false);
                setComment("");
                setFiles([]);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      <Dialog open={escalateOpen} onOpenChange={setEscalateOpen}>
        <DialogContent className="max-w-md" data-testid="dispute-escalate-dialog">
          <DialogHeader>
            <DialogTitle>Request formal mediation</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#5C4E48]">
            An admin will review the submitted evidence from both sides and post a verdict. Please briefly explain why you believe this needs mediation.
          </p>
          <Textarea
            rows={5}
            value={escalateReason}
            onChange={(e) => setEscalateReason(e.target.value)}
            placeholder="e.g. The customer's review contains a factual error that I've documented via the attached photos and audio message."
            className="rounded-lg"
            data-testid="dispute-escalate-reason"
          />
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setEscalateOpen(false)}
              className="rounded-lg"
            >
              Cancel
            </Button>
            <Button
              onClick={escalate}
              disabled={escalating}
              className="rounded-lg bg-[#B31E1E] hover:bg-[#8F1717] text-white"
              data-testid="dispute-escalate-submit"
            >
              {escalating ? "Escalating..." : "Submit for mediation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default DisputeBlock;
